import {faMap} from '@fortawesome/free-regular-svg-icons/faMap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useState} from 'react';
import PhoneInput from 'react-phone-number-input/input';
import HR from '../../../../common/components/HR';
import {useLayout} from '../../../../common/components/layout/LayoutProvider';
import {isMobile} from '../../../../common/helpers';
import {useI18n} from '../../../../common/providers/I18nProvider';
import SecondaryLink from '../../common/components/SecondaryLink';
import SubmitButton from '../../common/components/SubmitButton';
import {useAuth} from '../../providers/AuthProvider';
import MapModal from './map/MapModal';
import useProfile from './useProfile';
import Form from '../../common/components/Form';
import BackButtonBar from '../../common/components/BackButtonBar';
import FormControl from '../../common/components/FormControl';
import InputField, {getInputClass} from '../../common/components/InputField';

export default function Profile() {
	const {logout} = useAuth();
	const {loading, formik, canNotSubmit, isInvalid, phoneError, setPhone, setGeo} = useProfile();
	const [showModal, setShowModal] = useState(false);
	const {i18n} = useI18n();

	const {showAuthNavbar, setShowAuthNavbar} = useLayout();

	const submitButton = (
		<SubmitButton outerDiv={false} canNotSubmit={canNotSubmit} loading={loading}>
			{i18n(window.innerWidth >= 640 ? 'AUTH.PROFILE.BUTTON.CREATE' : 'AUTH.PROFILE.BUTTON.ACCOUNT')}
		</SubmitButton>
	);

	return (
		<>
			<BackButtonBar
				show={isMobile()}
				title='AUTH.PROFILE.TITLE'
				action={() => {
					setShowAuthNavbar(true);
					logout();
				}}
			/>

			<Form
				handleSubmit={formik.handleSubmit}
				status={formik.status}
				title='AUTH.PROFILE.TITLE'
				showTitle={showAuthNavbar}
				lineUnderTitle
			>
				<FormControl
					label='AUTH.PROFILE.FIELD.COMPANY.LABEL'
					error={formik.errors.companyName}
					isInvalid={!!isInvalid.companyName}
					required
				>
					<InputField
						fieldProps={formik.getFieldProps('companyName')}
						isInvalid={!!isInvalid.companyName}
						placeholder='AUTH.PROFILE.FIELD.COMPANY.PLACEHOLDER'
					/>
				</FormControl>

				<FormControl
					label='AUTH.PROFILE.FIELD.STORE.LABEL'
					error={formik.errors.storeName}
					isInvalid={!!isInvalid.storeName}
					required
				>
					<InputField
						fieldProps={formik.getFieldProps('storeName')}
						isInvalid={!!isInvalid.storeName}
						placeholder='AUTH.PROFILE.FIELD.STORE.PLACEHOLDER'
					/>
				</FormControl>

				<FormControl
					label='AUTH.PROFILE.FIELD.STORE.MANAGER_NAME.LABEL'
					error={formik.errors.managerName}
					isInvalid={!!isInvalid.managerName}
					required
				>
					<InputField
						fieldProps={formik.getFieldProps('managerName')}
						isInvalid={!!isInvalid.managerName}
						placeholder='AUTH.PROFILE.FIELD.STORE.MANAGER_NAME.PLACEHOLDER'
					/>
				</FormControl>

				<FormControl
					label='AUTH.PROFILE.FIELD.STORE.BUSINESS_EMAIL.LABEL'
					error={formik.errors.businessEmail}
					isInvalid={!!isInvalid.businessEmail}
					required
				>
					<InputField
						fieldProps={formik.getFieldProps('businessEmail')}
						isInvalid={!!isInvalid.businessEmail}
						placeholder='AUTH.PROFILE.FIELD.STORE.BUSINESS_EMAIL.PLACEHOLDER'
					/>
				</FormControl>

				<div className='mb-6 flex'>
					<HR />
				</div>

				<div className='mb-4 flex flex-col'>
					<h1 className='text-lg lg:text-xl font-bold text-black-10'>{i18n('AUTH.PROFILE.STORE.TITLE')}</h1>
				</div>

				<FormControl
					label='AUTH.PROFILE.FIELD.LOCATION.LABEL'
					error={formik.errors.location}
					isInvalid={!!isInvalid.location}
					required
				>
					<div
						className='relative w-full'
						role='button'
						tabIndex={0}
						onClick={() => {
							setShowModal(true);
						}}
					>
						<InputField
							fieldProps={formik.getFieldProps('location')}
							isInvalid={!!isInvalid.location}
							placeholder='AUTH.PROFILE.FIELD.LOCATION.PLACEHOLDER'
						/>
						<div className='absolute inset-y-0 right-0 flex items-center px-2'>
							<button
								type='button'
								className='px-2 py-1 text-sm text-gray-600 cursor-pointer'
								onClick={() => {
									setShowModal(true);
								}}
							>
								<FontAwesomeIcon icon={faMap} />
							</button>
						</div>
					</div>
				</FormControl>

				<FormControl
					label='AUTH.PROFILE.FIELD.ZIP.LABEL'
					error={formik.errors.zipCode}
					isInvalid={!!isInvalid.zipCode}
					required
				>
					<InputField
						fieldProps={formik.getFieldProps('zipCode')}
						isInvalid={!!isInvalid.zipCode}
						placeholder='AUTH.PROFILE.FIELD.ZIP.PLACEHOLDER'
					/>
				</FormControl>

				<div className='grid grid-cols-2 gap-4'>
					<FormControl
						label='AUTH.PROFILE.FIELD.PREFECTURE.LABEL'
						error={formik.errors.prefecture}
						isInvalid={!!isInvalid.prefecture}
						required
					>
						<InputField
							fieldProps={formik.getFieldProps('prefecture')}
							isInvalid={!!isInvalid.prefecture}
							placeholder='AUTH.PROFILE.FIELD.PREFECTURE.PLACEHOLDER'
						/>
					</FormControl>

					<FormControl
						label='AUTH.PROFILE.FIELD.STREET.LABEL'
						error={formik.errors.streetNo}
						isInvalid={!!isInvalid.streetNo}
						required
					>
						<InputField
							fieldProps={formik.getFieldProps('streetNo')}
							isInvalid={!!isInvalid.streetNo}
							placeholder='AUTH.PROFILE.FIELD.STREET.PLACEHOLDER'
						/>
					</FormControl>
				</div>

				<FormControl
					label='AUTH.PROFILE.FIELD.BUILDING.LABEL'
					error={formik.errors.buildingName}
					isInvalid={!!isInvalid.buildingName}
					required
				>
					<InputField
						fieldProps={formik.getFieldProps('buildingName')}
						isInvalid={!!isInvalid.buildingName}
						placeholder='AUTH.PROFILE.FIELD.BUILDING.PLACEHOLDER'
					/>
				</FormControl>

				<div className='mb-4 grid grid-cols-2 gap-4'>
					<FormControl
						label='AUTH.PROFILE.FIELD.STATION.LABEL'
						error={formik.errors.nearestStation}
						isInvalid={!!isInvalid.nearestStation}
					>
						<InputField
							fieldProps={formik.getFieldProps('nearestStation')}
							isInvalid={!!isInvalid.nearestStation}
							placeholder='AUTH.PROFILE.FIELD.STATION.PLACEHOLDER'
						/>
					</FormControl>

					<FormControl label='AUTH.PROFILE.FIELD.PHONE.LABEL' error={phoneError} isInvalid={!!phoneError} required>
						<PhoneInput
							placeholder={i18n('AUTH.PROFILE.FIELD.PHONE.PLACEHOLDER')}
							className={getInputClass(!!isInvalid.storePhone)}
							country='JP'
							onChange={(value) => {
								const phonNumber = value?.toString().slice(3, value.length);
								setPhone(value?.toString() ? `+810${phonNumber}` : '');
							}}
						/>
					</FormControl>
				</div>

				<div className='mb-4 flex'>
					<HR />
				</div>

				{isMobile() && submitButton}

				{!isMobile() && (
					<div className='flex justify-between gap-8'>
						<div className='w-full'>{submitButton}</div>
						<div className='w-full'>
							<SecondaryLink onClick={logout}>{i18n('AUTH.PROFILE.BUTTON.CANCEL')}</SecondaryLink>
						</div>
					</div>
				)}
			</Form>

			<MapModal
				show={showModal}
				close={() => setShowModal(false)}
				onSelect={(selected) => {
					formik.setFieldValue('location', selected.address);
					formik.setFieldValue('zipCode', selected.zipCode);
					formik.setFieldValue('prefecture', selected.prefecture);
					formik.setFieldValue('buildingName', selected.building);
					formik.setFieldValue('streetNo', selected.streetNo);
					setGeo(selected.geo);
					setShowModal(false);
				}}
			/>
		</>
	);
}
